import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	HostBinding,
	Input,
	OnDestroy,
	OnInit,
	Renderer2,
	TemplateRef,
	ViewEncapsulation,
} from "@angular/core";
import { coerceBoolean } from "@obg/ngx.global.evo";

import { AccordionDirective } from "../accordion.directive";

@Component({
	selector: "obg-accordion-content",
	templateUrl: "./accordion-content.component.html",
	styleUrls: ["./accordion-content.component.scss"],
	encapsulation: ViewEncapsulation.None,
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccordionContentComponent implements OnInit, OnDestroy {
	@HostBinding("class.obg-accordion-content") hostCssClass = true;

	@HostBinding("class.disable-contain-content")
	@Input()
	@coerceBoolean()
	disableContainContent = false;

	@Input() key: string | undefined;

	/**
	 * Whether the HTML content will not be removed from the DOM and only hidden.
	 */
	@Input()
	@coerceBoolean()
	hideOnly = false;

	/**
	 * If provided, the component will use `[ngTemplateOutlet]` instead of `<ng-content>` for content projection.
	 *
	 * It allows angular lifecycle hooks to execute on expand/collapse for components inside projected content,
	 * independently from this hosting component.
	 */
	@Input() templateRef: TemplateRef<unknown> | undefined;

	/**
	 * Explicit context can be set optionally for the projected content.
	 */
	@Input() templateContext: unknown;

	isExpanded = false;
	isContentVisible = false;
	animationEnabled = false;

	constructor(
		private accordion: AccordionDirective,
		private changeDetection: ChangeDetectorRef,
		private renderer: Renderer2,
		private el: ElementRef
	) {}

	ngOnInit() {
		if (this.key !== undefined) {
			this.accordion.register(this.key, { content: this });
		}
	}

	ngOnDestroy() {
		if (this.key !== undefined) {
			this.accordion.unregister(this.key);
		}
	}

	setDisplay(isContentVisible: boolean) {
		this.isContentVisible = isContentVisible;
		this.isContentVisible
			? this.renderer.removeStyle(this.el.nativeElement, "display")
			: this.renderer.setStyle(this.el.nativeElement, "display", "none");
	}

	/**
	 * Used only internally within a sub-component.
	 */
	$$toggleHandler(expand: boolean, animationEnabled: boolean) {
		this.isExpanded = expand;

		if (expand) {
			this.setDisplay(true);
		} else if (!animationEnabled) {
			this.setDisplay(false);
		}

		this.animationEnabled = animationEnabled;

		// we need to trigger this manual as sometimes the user may click too fast in the middle of
		// a change detection and will cause `ExpressionChangedAfterItHasBeenCheckedError`
		this.changeDetection.detectChanges();
	}
}
